import { useEffect, useRef } from 'react';

const RootCauseSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
          }
        });
      },
      { threshold: 0.1 }
    );
    const els = sectionRef.current?.querySelectorAll('.animate-on-scroll');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-24 bg-[#0d1120] w-full relative overflow-hidden" ref={sectionRef}>
      {/* 배경 장식 */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-orange-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[200px] bg-red-500/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-8 relative z-10">

        {/* 배지 */}
        <div className="animate-on-scroll opacity-0 translate-y-5 transition-all duration-600 flex justify-center mb-6">
          <span className="inline-flex items-center gap-2 bg-orange-500/15 text-orange-400 text-xs font-bold tracking-widest uppercase px-5 py-1.5 rounded-full border border-orange-500/25">
            <i className="ri-focus-3-line" />
            ROOT CAUSE
          </span>
        </div>

        {/* 서브 타이틀 */}
        <div className="animate-on-scroll opacity-0 translate-y-5 transition-all duration-600 delay-75 text-center mb-4">
          <p className="text-gray-400 text-sm font-medium tracking-widest uppercase">
            이 모든 문제의 원인
          </p>
        </div>

        {/* 메인 큰 텍스트 */}
        <div className="animate-on-scroll opacity-0 translate-y-6 transition-all duration-700 delay-100 text-center mb-14">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white leading-tight">
            실리콘{' '}
<span className="text-orange-400">&apos;별도 도포 작업&apos;</span>
            {' '}방식의
            <br />
            <span className="text-gray-300">구조적 한계</span>
          </h2>
        </div>

        {/* 문제 요약 카드 2개 */}
        <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-200 grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
          {/* 카드 1 */}
          <div className="group bg-white/[0.04] hover:bg-white/[0.07] border border-white/10 hover:border-red-500/30 rounded-2xl p-7 flex items-start gap-5 transition-all duration-300">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-red-500/15 border border-red-500/20 flex-shrink-0 group-hover:bg-red-500/25 transition-colors duration-300">
              <i className="ri-error-warning-fill text-red-400 text-xl" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-red-400 text-[10px] font-bold tracking-widest uppercase bg-red-500/10 px-2 py-0.5 rounded-full border border-red-500/20">PROBLEM 01</span>
              </div>
              <p className="text-white font-bold text-base mb-2">실리콘 균열 및 이물질 축적</p>
              <p className="text-gray-400 text-sm leading-relaxed">경화 후 수축·균열로 방수 기능 상실</p>
            </div>
          </div>

          {/* 카드 2 */}
          <div className="group bg-white/[0.04] hover:bg-white/[0.07] border border-white/10 hover:border-sky-500/30 rounded-2xl p-7 flex items-start gap-5 transition-all duration-300">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-sky-500/15 border border-sky-500/20 flex-shrink-0 group-hover:bg-sky-500/25 transition-colors duration-300">
              <i className="ri-drop-fill text-sky-400 text-xl" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sky-400 text-[10px] font-bold tracking-widest uppercase bg-sky-500/10 px-2 py-0.5 rounded-full border border-sky-500/20">PROBLEM 02</span>
              </div>
              <p className="text-white font-bold text-base mb-2">볼트 주변 누수 발생</p>
              <p className="text-gray-400 text-sm leading-relaxed">접착력 상실로 단열재까지 빗물 침투</p>
            </div>
          </div>
        </div>

        {/* 구분 화살표 */}
        <div className="animate-on-scroll opacity-0 transition-all duration-500 delay-300 flex flex-col items-center gap-2 mb-10">
          <div className="w-px h-8 bg-gradient-to-b from-white/20 to-transparent" />
          <div className="w-8 h-8 flex items-center justify-center rounded-full border border-white/10 bg-white/5">
            <i className="ri-arrow-down-line text-white/40 text-sm" />
          </div>
          <div className="w-px h-4 bg-gradient-to-b from-green-500/30 to-transparent" />
        </div>

        {/* 해결책 강조 배너 */}
        <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-400">
          <div className="relative bg-gradient-to-r from-green-500/10 via-emerald-500/10 to-green-500/10 border border-green-500/30 rounded-2xl px-8 py-8 text-center overflow-hidden">
            {/* 배경 광택 */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-1 bg-gradient-to-r from-transparent via-green-400/50 to-transparent rounded-full" />

            <div className="flex justify-center mb-5">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-green-500/20 border border-green-500/30">
                <i className="ri-shield-check-fill text-green-400 text-2xl" />
              </div>
            </div>

            <p className="text-white text-base md:text-lg leading-relaxed">
              <span className="text-green-400 font-extrabold text-lg md:text-xl">✅ Smart Cap</span>
              <span className="text-white">은 실리콘 패킹이 </span>
              <span className="text-green-300 font-bold">일체형으로 내장</span>
              <span className="text-white">되어</span>
            </p>
            <p className="text-green-300 font-extrabold text-lg md:text-xl mt-2">
              이런 문제가 원천적으로 발생하지 않습니다
            </p>
          </div>
        </div>

      </div>
    </section>
  );
};

export default RootCauseSection;
